import type { PresupuestoObra } from './tipos'

/**
 * Genera un archivo Excel (.xls formato XML Spreadsheet 2003)
 * compatible con todas las versiones de Excel, con estilos y formato monetario.
 */
export function exportarPresupuestoExcel(presupuesto: PresupuestoObra) {
  let filasXml = ''

  // Encabezado corporativo
  filasXml += `
    <Row>
      <Cell ss:MergeAcross="6" ss:StyleID="TituloPrincipal"><Data ss:Type="String">MEGA DISTRIBUCIONES S.A.S.</Data></Cell>
    </Row>
    <Row>
      <Cell ss:MergeAcross="6" ss:StyleID="Subtitulo"><Data ss:Type="String">PRESUPUESTO DE OBRA Y MONTAJE ELÉCTRICO</Data></Cell>
    </Row>
    <Row><Cell><Data ss:Type="String"></Data></Cell></Row>
    <Row>
      <Cell ss:StyleID="EtiquetaInfo"><Data ss:Type="String">CÓDIGO:</Data></Cell>
      <Cell ss:StyleID="ValorInfo"><Data ss:Type="String">${escapeXml(presupuesto.codigo)}</Data></Cell>
      <Cell ss:StyleID="EtiquetaInfo"><Data ss:Type="String">FECHA:</Data></Cell>
      <Cell ss:StyleID="ValorInfo"><Data ss:Type="String">${new Date(presupuesto.updated_at || presupuesto.created_at).toLocaleDateString('es-CO')}</Data></Cell>
    </Row>
    <Row>
      <Cell ss:StyleID="EtiquetaInfo"><Data ss:Type="String">PROYECTO:</Data></Cell>
      <Cell ss:MergeAcross="2" ss:StyleID="ValorInfo"><Data ss:Type="String">${escapeXml(presupuesto.nombre)}</Data></Cell>
      <Cell ss:StyleID="EtiquetaInfo"><Data ss:Type="String">ESTADO:</Data></Cell>
      <Cell ss:StyleID="ValorInfo"><Data ss:Type="String">${escapeXml(presupuesto.estado.toUpperCase())}</Data></Cell>
    </Row>
    <Row>
      <Cell ss:StyleID="EtiquetaInfo"><Data ss:Type="String">CLIENTE:</Data></Cell>
      <Cell ss:MergeAcross="2" ss:StyleID="ValorInfo"><Data ss:Type="String">${escapeXml(presupuesto.cliente || 'GENERAL')}</Data></Cell>
      <Cell ss:StyleID="EtiquetaInfo"><Data ss:Type="String">UBICACIÓN:</Data></Cell>
      <Cell ss:StyleID="ValorInfo"><Data ss:Type="String">${escapeXml(presupuesto.ubicacion || 'N/A')}</Data></Cell>
    </Row>
    <Row><Cell><Data ss:Type="String"></Data></Cell></Row>
  `

  // Capítulos e Ítems
  presupuesto.capitulos.forEach((cap, capIdx) => {
    filasXml += `
      <Row>
        <Cell ss:MergeAcross="5" ss:StyleID="EncabezadoCapitulo"><Data ss:Type="String">${escapeXml(cap.nombre)}</Data></Cell>
        <Cell ss:StyleID="SubtotalCapituloHeader"><Data ss:Type="Number">${cap.subtotal_capitulo}</Data></Cell>
      </Row>
      <Row>
        <Cell ss:StyleID="ColHeader"><Data ss:Type="String">ÍTEM</Data></Cell>
        <Cell ss:StyleID="ColHeader"><Data ss:Type="String">CÓDIGO APU</Data></Cell>
        <Cell ss:StyleID="ColHeader"><Data ss:Type="String">DESCRIPCIÓN DE ACTIVIDAD</Data></Cell>
        <Cell ss:StyleID="ColHeader"><Data ss:Type="String">UND</Data></Cell>
        <Cell ss:StyleID="ColHeader"><Data ss:Type="String">CANTIDAD</Data></Cell>
        <Cell ss:StyleID="ColHeader"><Data ss:Type="String">V. UNITARIO</Data></Cell>
        <Cell ss:StyleID="ColHeader"><Data ss:Type="String">SUBTOTAL</Data></Cell>
      </Row>
    `

    cap.items.forEach((item, itemIdx) => {
      filasXml += `
        <Row>
          <Cell ss:StyleID="ItemCentro"><Data ss:Type="String">${capIdx + 1}.${itemIdx + 1}</Data></Cell>
          <Cell ss:StyleID="ItemCentro"><Data ss:Type="String">${escapeXml(item.codigo)}</Data></Cell>
          <Cell ss:StyleID="ItemTexto"><Data ss:Type="String">${escapeXml(item.nombre)}</Data></Cell>
          <Cell ss:StyleID="ItemCentro"><Data ss:Type="String">${escapeXml(item.unidad)}</Data></Cell>
          <Cell ss:StyleID="ItemNumero"><Data ss:Type="Number">${item.cantidad}</Data></Cell>
          <Cell ss:StyleID="ItemMoneda"><Data ss:Type="Number">${item.precio_unitario}</Data></Cell>
          <Cell ss:StyleID="ItemMoneda"><Data ss:Type="Number">${item.subtotal}</Data></Cell>
        </Row>
      `
    })

    filasXml += `
      <Row>
        <Cell ss:MergeAcross="5" ss:StyleID="FilaSubtotalLabel"><Data ss:Type="String">SUBTOTAL ${escapeXml(cap.nombre.toUpperCase())}:</Data></Cell>
        <Cell ss:StyleID="FilaSubtotalValor"><Data ss:Type="Number">${cap.subtotal_capitulo}</Data></Cell>
      </Row>
      <Row><Cell><Data ss:Type="String"></Data></Cell></Row>
    `
  })

  // Resumen AIU y Gran Total
  const aiu = presupuesto.aiu || {
    porcentaje_admin: 10,
    porcentaje_imprevistos: 0,
    porcentaje_utilidad: 3,
    porcentaje_iva_utilidad: 19,
  }
  const cDirecto = presupuesto.costo_directo_total
  const valAdmin = cDirecto * ((aiu.porcentaje_admin || 0) / 100)
  const valImprev = cDirecto * ((aiu.porcentaje_imprevistos || 0) / 100)
  const valUtil = cDirecto * ((aiu.porcentaje_utilidad || 0) / 100)
  const valIvaUtil = valUtil * ((aiu.porcentaje_iva_utilidad || 19) / 100)

  filasXml += `
    <Row><Cell><Data ss:Type="String"></Data></Cell></Row>
    <Row>
      <Cell ss:MergeAcross="4"><Data ss:Type="String"></Data></Cell>
      <Cell ss:StyleID="ResumenLabel"><Data ss:Type="String">COSTO DIRECTO TOTAL:</Data></Cell>
      <Cell ss:StyleID="ResumenValor"><Data ss:Type="Number">${cDirecto}</Data></Cell>
    </Row>
    <Row>
      <Cell ss:MergeAcross="4"><Data ss:Type="String"></Data></Cell>
      <Cell ss:StyleID="ResumenLabel"><Data ss:Type="String">ADMINISTRACIÓN (${aiu.porcentaje_admin}%):</Data></Cell>
      <Cell ss:StyleID="ResumenValor"><Data ss:Type="Number">${valAdmin}</Data></Cell>
    </Row>
    <Row>
      <Cell ss:MergeAcross="4"><Data ss:Type="String"></Data></Cell>
      <Cell ss:StyleID="ResumenLabel"><Data ss:Type="String">IMPREVISTOS (${aiu.porcentaje_imprevistos}%):</Data></Cell>
      <Cell ss:StyleID="ResumenValor"><Data ss:Type="Number">${valImprev}</Data></Cell>
    </Row>
    <Row>
      <Cell ss:MergeAcross="4"><Data ss:Type="String"></Data></Cell>
      <Cell ss:StyleID="ResumenLabel"><Data ss:Type="String">UTILIDAD (${aiu.porcentaje_utilidad}%):</Data></Cell>
      <Cell ss:StyleID="ResumenValor"><Data ss:Type="Number">${valUtil}</Data></Cell>
    </Row>
    <Row>
      <Cell ss:MergeAcross="4"><Data ss:Type="String"></Data></Cell>
      <Cell ss:StyleID="ResumenLabel"><Data ss:Type="String">IVA SOBRE UTILIDAD (${aiu.porcentaje_iva_utilidad}%):</Data></Cell>
      <Cell ss:StyleID="ResumenValor"><Data ss:Type="Number">${valIvaUtil}</Data></Cell>
    </Row>
    <Row>
      <Cell ss:MergeAcross="4"><Data ss:Type="String"></Data></Cell>
      <Cell ss:StyleID="GranTotalLabel"><Data ss:Type="String">TOTAL PRESUPUESTO:</Data></Cell>
      <Cell ss:StyleID="GranTotalValor"><Data ss:Type="Number">${presupuesto.total_presupuesto}</Data></Cell>
    </Row>
  `

  const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal">
   <Alignment ss:Vertical="Center"/>
   <Borders/>
   <Font ss:FontName="Calibri" ss:Size="11" ss:Color="#1E293B"/>
   <Interior/>
   <NumberFormat/>
   <Protection/>
  </Style>
  <Style ss:ID="TituloPrincipal">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="16" ss:Bold="1" ss:Color="#4338CA"/>
  </Style>
  <Style ss:ID="Subtitulo">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="12" ss:Bold="1" ss:Color="#64748B"/>
  </Style>
  <Style ss:ID="EtiquetaInfo">
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#334155"/>
  </Style>
  <Style ss:ID="ValorInfo">
   <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#0F172A"/>
  </Style>
  <Style ss:ID="EncabezadoCapitulo">
   <Font ss:FontName="Calibri" ss:Size="12" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#312E81" ss:Pattern="Solid"/>
   <Alignment ss:Vertical="Center" ss:Indent="1"/>
  </Style>
  <Style ss:ID="SubtotalCapituloHeader">
   <Font ss:FontName="Calibri" ss:Size="12" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#312E81" ss:Pattern="Solid"/>
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
   <NumberFormat ss:Format="$#,##0"/>
  </Style>
  <Style ss:ID="ColHeader">
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#4F46E5" ss:Pattern="Solid"/>
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
   </Borders>
  </Style>
  <Style ss:ID="ItemCentro">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#F1F5F9"/>
   </Borders>
  </Style>
  <Style ss:ID="ItemTexto">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center" ss:WrapText="1"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#F1F5F9"/>
   </Borders>
  </Style>
  <Style ss:ID="ItemNumero">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
   <NumberFormat ss:Format="#,##0.00"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#F1F5F9"/>
   </Borders>
  </Style>
  <Style ss:ID="ItemMoneda">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
   <NumberFormat ss:Format="$#,##0"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#F1F5F9"/>
   </Borders>
  </Style>
  <Style ss:ID="FilaSubtotalLabel">
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#1E293B"/>
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
   <Interior ss:Color="#EEF2FF" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="FilaSubtotalValor">
   <Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1" ss:Color="#4338CA"/>
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
   <Interior ss:Color="#EEF2FF" ss:Pattern="Solid"/>
   <NumberFormat ss:Format="$#,##0"/>
  </Style>
  <Style ss:ID="ResumenLabel">
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#475569"/>
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
  </Style>
  <Style ss:ID="ResumenValor">
   <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#0F172A"/>
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
   <NumberFormat ss:Format="$#,##0"/>
  </Style>
  <Style ss:ID="GranTotalLabel">
   <Font ss:FontName="Calibri" ss:Size="12" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#10B981" ss:Pattern="Solid"/>
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
  </Style>
  <Style ss:ID="GranTotalValor">
   <Font ss:FontName="Calibri" ss:Size="13" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#10B981" ss:Pattern="Solid"/>
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
   <NumberFormat ss:Format="$#,##0"/>
  </Style>
 </Styles>
 <Worksheet ss:Name="Presupuesto">
  <Table ss:DefaultRowHeight="20">
   <Column ss:Width="45"/>
   <Column ss:Width="90"/>
   <Column ss:Width="380"/>
   <Column ss:Width="50"/>
   <Column ss:Width="80"/>
   <Column ss:Width="110"/>
   <Column ss:Width="120"/>
   ${filasXml}
  </Table>
 </Worksheet>
</Workbook>`

  const blob = new Blob([xmlContent], { type: 'application/vnd.ms-excel;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${presupuesto.codigo}_${presupuesto.nombre.replace(/[^a-zA-Z0-9_-]/g, '_')}.xls`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function escapeXml(unsafe: string): string {
  if (!unsafe) return ''
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

